import React, { useCallback, useEffect } from 'react';
import Popover from '@mui/material/Popover';
import { Button, Box, Typography, IconButton } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'common/hook';
import FormProvider from 'common/components/form-provider';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { campaignOverviewFilterSchema } from 'common/schema';
import {
    setCampaignOverviewSearchForm,
    clearCampaignOverviewSearchForm,
} from 'slices/campaignsSlice';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import { useTranslation } from 'react-i18next';
import CampaignConstants from 'pages/admin/campaign/constants';
import { createStyleSheet } from 'utils/helpers';
import SyncIcon from '@mui/icons-material/Sync';
import { RootState } from 'plugins/store';

interface MainHeaderCampaignFilterPopoverProps {
    open: boolean;
    headerFilterId: string | undefined;
    anchorEl: HTMLButtonElement | null;
    handleClose: () => void;
}

const CampaignOverviewFilter: React.FC<
    MainHeaderCampaignFilterPopoverProps
> = ({ open, headerFilterId, anchorEl, handleClose }) => {
    const dispatch = useAppDispatch();
    const campaignOverviewSearchForm = useAppSelector(
        (state: RootState) => state.campaignState.campaignOverviewSearchForm
    );
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(campaignOverviewFilterSchema),
        defaultValues: {
            service: campaignOverviewSearchForm.service || [
                CampaignConstants.service.ALL,
            ],
            device: campaignOverviewSearchForm.device || [
                CampaignConstants.device.ALL,
            ],
            area: campaignOverviewSearchForm.area || [
                CampaignConstants.area.ALL,
            ],
            startDate: campaignOverviewSearchForm.startDate || '',
            endDate: campaignOverviewSearchForm.endDate || '',
        },
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const onSubmit = handleSubmit(async (data) => {
        const params = { ...data };
        dispatch(setCampaignOverviewSearchForm(params));
        handleClose();

        return;
    });

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    const handleSelectItemWithAll = useCallback(
        (
            itemName: 'service' | 'device' | 'area' | 'startDate' | 'endDate',
            watchItem: any[] | undefined, // eslint-disable-line
            allValue: string,
            constants: Record<string, string>
        ) => {
            if (watchItem) {
                // check if the last item is ALL, remove all other items
                if (
                    watchItem.length > 1 &&
                    watchItem[watchItem.length - 1] === allValue
                ) {
                    setValue(itemName, [allValue]);
                    return;
                }

                // check if included ALL, and the last item is not ALL, remove ALL
                if (
                    watchItem.includes(allValue) &&
                    watchItem[watchItem.length - 1] !== allValue
                ) {
                    setValue(
                        itemName,
                        watchItem.filter((item: string) => item !== allValue)
                    );
                    return;
                }

                // if not include ALL, and the number = constants.length - 1, set to ALL
                if (
                    !watchItem.includes(allValue) &&
                    watchItem.length === Object.keys(constants).length - 1
                ) {
                    setValue(itemName, [allValue]);
                    return;
                }
            }
        },
        [setValue]
    );
    const watchService = watch('service');
    useEffect(() => {
        handleSelectItemWithAll(
            'service',
            watchService,
            CampaignConstants.service.ALL,
            CampaignConstants.service
        );
    }, [handleSelectItemWithAll, watchService]);
    const watchDevice = watch('device');
    useEffect(() => {
        handleSelectItemWithAll(
            'device',
            watchDevice,
            CampaignConstants.device.ALL,
            CampaignConstants.device
        );
    }, [handleSelectItemWithAll, watchDevice]);
    const watchArea = watch('area');
    useEffect(() => {
        handleSelectItemWithAll(
            'area',
            watchArea,
            CampaignConstants.area.ALL,
            CampaignConstants.area
        );
    }, [handleSelectItemWithAll, watchArea]);
    useEffect(() => {
        if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            if (start > end) {
                setValue('endDate', '');
            }
        }
    }, [startDate, endDate, setValue]);
    const handleRenewForm = () => {
        dispatch(clearCampaignOverviewSearchForm());
        reset({
            service: [CampaignConstants.service.ALL],
            device: [CampaignConstants.device.ALL],
            area: [CampaignConstants.area.ALL],
            startDate: '',
            endDate: '',
        });
    };

    const topPixel = 104;

    const datePickerStyle = {
        '& .MuiInputBase-root': {
            // width: '156px',
            height: '29px',
            borderRadius: '10px',
        },
        '& .MuiIconButton-root svg': {
            width: '14px',
            height: '14px',
        },
    };

    const checkBoxStyle = {
        '& .MuiFormControlLabel-label': {
            fontSize: '20px',
        },
    };

    const groupTitleStyle = {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '29px',
        marginBottom: '15px',
    };

    return (
        <Popover
            id={headerFilterId}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            sx={{
                '& .MuiPaper-root.MuiPopover-paper': {
                    top: `${topPixel}px !important`,
                    maxHeight: `min(906px, calc(100% - ${topPixel}px))`,
                    borderRadius: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box
                    sx={{
                        padding: '20px 30px 85px',
                        width: '430px',
                        overflowY: 'auto',
                        maxHeight: '700px',
                    }}
                >
                    <Box
                        mb={'20px'}
                        display={'flex'}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Typography
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                                fontWeight: 800,
                                fontSize: '27px',
                                lineHeight: '33px',
                            }}
                        >
                            {t('layout.header.filter.title')}
                        </Typography>
                        <IconButton
                            onClick={handleRenewForm}
                            aria-label="refresh"
                        >
                            <SyncIcon
                                sx={{
                                    fontSize: 32,
                                    color: 'var(--color-primary)',
                                }}
                            />
                        </IconButton>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thời gian
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            gap={1}
                        >
                            {t('layout.header.filter.time.start')}
                            <BaseDatePicker
                                name="startDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                            {t('layout.header.filter.time.end')}
                            <BaseDatePicker
                                name="endDate"
                                size="small"
                                sx={datePickerStyle}
                                onKeyDown={(e) => {
                                    e.preventDefault();
                                }}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Loại dữ liệu
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="service"
                                options={
                                    Object.keys(CampaignConstants.service).map(
                                        (service: string) => ({
                                            label: t(
                                                `page.campaign.service.${service.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.service[
                                                service as keyof typeof CampaignConstants.service
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Thiết bị
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="device"
                                options={
                                    Object.keys(CampaignConstants.device).map(
                                        (device: string) => ({
                                            label: t(
                                                `page.campaign.device.${device.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.device[
                                                device as keyof typeof CampaignConstants.device
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                    <Box mb={'33px'}>
                        <Box>
                            <Typography variant="h6" sx={groupTitleStyle}>
                                Khu vực
                            </Typography>
                        </Box>
                        <Box>
                            <BaseMultiCheckbox
                                name="area"
                                options={
                                    Object.keys(CampaignConstants.area).map(
                                        (area: string) => ({
                                            label: t(
                                                `page.campaign.area.${area.toLowerCase()}`
                                            ),
                                            value: CampaignConstants.area[
                                                area as keyof typeof CampaignConstants.area
                                            ],
                                        })
                                    ) as { label: string; value: string }[]
                                }
                                sx={checkBoxStyle}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box
                    display={'flex'}
                    justifyContent={'end'}
                    gap={2}
                    padding={'20px 30px'}
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        style={styles.modalButton}
                    >
                        {t('common.close')}
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        aria-label="filter"
                        onClick={onSubmit}
                        style={styles.modalButton}
                    >
                        {t('layout.header.filter.title')}
                    </Button>
                </Box>
            </FormProvider>
        </Popover>
    );
};

const styles = createStyleSheet({
    modalButton: {
        textTransform: 'none',
        borderRadius: '40px',
        minHeight: '42px',
        minWidth: '84px',
        fontSize: '14px',
        fontWeight: '600',
        borderWidth: '2px',
    },
});

export default CampaignOverviewFilter;
